import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatCurrency, useTranslate } from "helpers";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import { getOverviewTotalVehiclesAPI } from "services/api/dashboard/api";
import { OverviewTotalVehiclesResponse } from "services/api/dashboard/types";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../common/DashboardTooltip";
import CardHeader, { DeltaBadge } from "../../common/CardHeader";
import { BarTooltipRow, BarTooltipTitle, LegendTooltip } from "../../common/HoverPopup";
import { compact, text } from "../../common/dashboardPalette";

const TotalVehiclesCard: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [data, setData] = useState<OverviewTotalVehiclesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getOverviewTotalVehiclesAPI()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <div className="relative h-full"><Loader isLoading={true}> </Loader></div>;
  if (!data) return <div className="flex items-center justify-center h-full text-sm text-slate-400">Nema podataka</div>;

  const total = data.total;
  const totalNew = data.newVehicles;
  const totalUsed = data.usedVehicles;
  const newChange = data.newChange;
  const usedChange = data.usedChange;
  const maxVal = Math.max(totalNew, totalUsed, 1);

  // Boje usklađene s RevenueExpenseCard (positive #10b981 = emerald-500, negative #f43f5e = rose-500).
  // Prozirni gradient jak na vrhu -> blijedi prema dnu (okrenut, kao površina grafa) + 2px border na vrhu (kao linija grafa).
  const newBar = "bg-gradient-to-b from-emerald-500/70 to-emerald-500/30 border-t-2 border-emerald-500 group-hover/bar:from-emerald-500/80 group-hover/bar:to-emerald-500/40";
  const usedBar = "bg-gradient-to-b from-rose-500/70 to-rose-500/30 border-t-2 border-rose-500 group-hover/bar:from-rose-500/80 group-hover/bar:to-rose-500/40";
  const newDot = "bg-emerald-500";
  const usedDot = "bg-rose-500";

  const categories = [
    { label: "Nova", value: totalNew, barClass: newBar, dotClass: newDot, change: newChange, condition: "new" },
    { label: "Rabljena", value: totalUsed, barClass: usedBar, dotClass: usedDot, change: usedChange, condition: "used" },
  ];

  return (
    <div className={`rounded-sm ${compact.padding} bg-white h-full flex flex-col`}>
      <CardHeader
        title="Vozila"
        subtitle={t("admin.dashboard.widget.overview.totalvehicles")}
        className="mb-10"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help">
                <span className={`${compact.metric} font-bold ${text.primary}`}>{total}</span>
                {data.soldThisMonth > 0 && (
                  <span className={`text-sm ${text.muted}`}>
                    {data.soldThisMonth} prodano u {new Date().toLocaleDateString("hr-HR", { month: "long" })}
                  </span>
                )}
              </div>
            }
          >
            <TooltipTitle>Pregled flote</TooltipTitle>
            <TooltipRow label="Ukupno" value={total} />
            <TooltipRow label="Nova vozila" value={totalNew} />
            <TooltipRow label="Rabljena vozila" value={totalUsed} />
            <TooltipDivider />
            <TooltipRow label="Prosječna nabavna" value={formatCurrency.format(data.avgPurchasePrice)} />
            <TooltipRow label="Prodano (tekući mj.)" value={data.soldThisMonth} emphasis="positive" />
          </DashboardTooltip>
        }
      />

      {data.total === 0 ? (
        <NoResults title="Nema vozila" />
      ) : (
        <>
      <div className="mx-auto mb-3 w-full flex-1 flex flex-col">
        <div className="flex justify-center gap-4 mb-2 flex-1 min-h-[120px]">
          {categories.map((cat) => {
            const heightPercent = (cat.value / maxVal) * 100;
            return (
              <div
                key={cat.label}
                className="flex flex-col items-center gap-1 flex-1 max-w-[60px] h-full cursor-pointer group/bar relative"
                onClick={() => navigate(`${FleetVehicleRoutes.FleetVehicleSearchPageRouter}?condition=${cat.condition}`)}
              >
                <span className={`text-sm ${text.muted}`}>{cat.value}</span>
                <div className="w-full flex-1 flex items-end">
                  <div
                    className={`w-full rounded-sm origin-bottom animate-growUp transition-all ${cat.barClass}`}
                    style={{ height: `${Math.max(heightPercent, 5)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-center gap-4">
          {categories.map((cat) => (
            <span key={cat.label} className={`text-sm ${text.subtle} flex-1 max-w-[60px] text-center`}>{cat.label}</span>
          ))}
        </div>
      </div>

      <div className="relative group flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-3 cursor-help">
        {categories.map((cat) => (
          <div key={cat.label} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-sm ${cat.dotClass}`} />
            <span className={`text-sm ${text.muted}`}>
              {cat.label} <span className={`font-medium ${text.secondary}`}>{cat.value}</span>
            </span>
            <DeltaBadge value={cat.change} />
          </div>
        ))}
        <LegendTooltip position="top">
          <BarTooltipTitle>Novo dodana vozila</BarTooltipTitle>
          <div className="text-xs text-slate-400 mb-2">
            Postotak uspoređuje broj vozila dodanih ovaj mjesec s brojem dodanih prošli mjesec.
          </div>
          {categories.map((cat) => (
            <BarTooltipRow
              key={cat.label}
              label={cat.label}
              value={`${cat.change >= 0 ? "+" : ""}${cat.change.toFixed(1)}%`}
              emphasis={cat.change >= 0 ? "positive" : "negative"}
            />
          ))}
        </LegendTooltip>
      </div>
        </>
      )}
    </div>
  );
};

export default TotalVehiclesCard;
